import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useBotContext } from '../useBotContext';
import { useOpeningAccessory } from '../../hooks/useOpeningAccessory';
import { useDrainPanAccessory } from '../../hooks/useDrainPanAccessory';
import { useAccessDoorAccessory } from '../../hooks/useAccessDoorAccessory';
import Select from '../atom/Select';
import FilterSectionFields from './filter';
import CoilSectionFields from './coil';
import FanSectionFields from './fan';
import RotaryHtexSectionFields from './rotary_htex';
import {
  getBotSectionOptions,
  applyTypeDefaults,
  applyCommonDefaults,
  resetBotFields,
  createBotSection,
  canChangeToFan,
  applyFanSectionRules,
  shouldShowFanOption,
  applyOtherSectionRules,
  applyAddReturnAirflowRules,
  applyAddOutsideAirflowRules,
  getAHURules,
  checkIsMixedAirPlenum,
  canRemoveFanSection,
} from '../../helpers/data/bot';
import { BotAHUSectionProps, IBotData, IBotSection, SectionFieldBaseProps } from '../../models';
import type { IState } from '../../../../models';
import './styles.scss';

const SectionFieldsMap: Partial<
  Record<IBotSection['type'], FC<SectionFieldBaseProps>>
> = {
  fan: FanSectionFields,
  filter: FilterSectionFields,
  coil: CoilSectionFields,
  er_wheel: RotaryHtexSectionFields,
};
const BotAHUSectionComponent: FC<BotAHUSectionProps> = ({
  tunnelIndex,
  sectionIndex,
  section,
  onSelect,
  isSelected,
}) => {
  const components = useSelector((state: IState) => state.ahu.components);
  const { value: botData, setValue } = useBotContext();
  const selectRef = useRef<any>(null);
  /* --------------------------- OPTIONS FOR SELECT -------------------------- */
  const sectionOptions = useMemo(() => {
    let options = getBotSectionOptions(botData, tunnelIndex);
    const tunnelSections = botData.tunnels?.[tunnelIndex]?.sections ?? [];
    const erWheelInUse = tunnelSections.some(
      (sec) => sec.type === 'er_wheel' && sec.id !== section.id,
    );
    // If current section is er_wheel, ensure er_wheel option is included for label display
    if (
      section.type === 'er_wheel' &&
      !options.some((o) => o.value === 'er_wheel')
    ) {
      options = [
        ...options,
        { value: 'er_wheel', label: 'Rotary Heat-Exchanger' },
      ];
    }

    return options
      .filter((o) => components?.some((c) => c.value === o.value))
      .filter((o) => (o.value === 'er_wheel' ? !erWheelInUse : true))
      .filter((o) =>
        o.value === 'fan'
          ? shouldShowFanOption(botData, tunnelIndex, section.id)
          : true,
      );
  }, [
    botData.tunnel_configuration,
    botData.tunnels,
    botData.air_system_id,
    botData.sa_ea_intake_configuration,
    tunnelIndex,
    section.id,
    section.type,
    components,
  ]);
  const isMixedAirPlenum = useMemo(() => {
    return checkIsMixedAirPlenum(sectionIndex, botData.air_system_id);
  }, [
    sectionIndex,
    botData.air_system_id,
    botData.tunnel_configuration,
    botData.tunnels[tunnelIndex].airflow_direction_id,
  ]);
  /* ------------------------------- SET DEFAULTS ------------------------------ */
  useEffect(() => {
    if (!['fan', 'coil', 'filter', 'er_wheel'].includes(section.type)) return;
    setValue((draft: IBotData) => {
      const target = draft.tunnels?.[tunnelIndex]?.sections?.[sectionIndex];
      if (!target) return;
      applyCommonDefaults(target);
      applyTypeDefaults(section.type, target);
    });
  }, [section.type, setValue, tunnelIndex, sectionIndex]);

  /* ------------------------- AUTO-MANAGE ACCESSORIES ------------------------- */

  useOpeningAccessory(tunnelIndex, sectionIndex);
  useDrainPanAccessory(
    tunnelIndex,
    sectionIndex,
    botData.tunnel_configuration,
    section,
    botData.tunnels?.[tunnelIndex].airflow_direction_id,
  );
  useAccessDoorAccessory(tunnelIndex, sectionIndex, section);

  /* ------------------------------- TYPE CHANGE ------------------------------- */
  const handleTypeChange = useCallback(
    (
      value: string | number | null,
      _: string,
      __?: string | undefined,
      name?: string,
    ) => {
      const newType = (value ?? '') as IBotSection['type'];
      setValue((draft: IBotData) => {
        const target = draft.tunnels?.[tunnelIndex]?.sections?.[sectionIndex];
        if (!target) return;
        const oldType = target.type;

        // Get rules to check if rotary_htex is enabled
        const airSystemId = draft.air_system_id ?? 1;
        const tunnelConfig = draft.tunnel_configuration ?? 1;
        const saEaConfig = draft.sa_ea_intake_configuration;
        const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

        // Prevent changing type away from er_wheel in tunnel 0 if rotary_htex is required
        if (
          oldType === 'er_wheel' &&
          tunnelIndex === 0 &&
          rules?.rotary_htex === true &&
          newType !== 'er_wheel'
        ) {
          // Cannot change away from er_wheel when rotary_htex rule is active
          return;
        }

        // Prevent changing type of protected er_wheel in tunnel 1
        if (
          oldType === 'er_wheel' &&
          tunnelIndex === 1 &&
          draft.tunnels.length > 0
        ) {
          const tunnel0 = draft.tunnels[0];
          if (tunnel0?.sections.some((s) => s.type === 'er_wheel')) {
            // This is a protected er_wheel, prevent type change
            return;
          }
        }
        // Prevent 2nd ER wheel in same tunnel
        if (newType === 'er_wheel') {
          const exists = draft.tunnels?.[tunnelIndex]?.sections?.some(
            (s, idx) => s.type === 'er_wheel' && idx !== sectionIndex,
          );
          if (exists) return;
        }
        // Check if changing to 'fan' is allowed based on configuration rules
        if (newType === 'fan' && !canChangeToFan(draft, tunnelIndex)) {
          return; // Block the change
        }
        // Remove drain_pan if changing from coil to another type
        if (oldType === 'coil' && newType !== 'coil') {
          target.accessories ||= [];
          const drainPanIndex = target.accessories.findIndex(
            (acc) => acc.type === 'drain_pan',
          );
          if (drainPanIndex !== -1) {
            target.accessories.splice(drainPanIndex, 1);
          }
        }

        target.type = newType;
        target.name = name || '';
        resetBotFields(target);
        applyCommonDefaults(target);
        applyTypeDefaults(newType, target);
        // When Rotary Htex (er_wheel) is selected in first tunnel (tunnelIndex 0),
        // automatically add it to the second tunnel (tunnelIndex 1)
        if (
          newType === 'er_wheel' &&
          tunnelIndex === 0 &&
          draft.tunnels.length > 1
        ) {
          const tunnel1 = draft.tunnels[1];
          if (tunnel1) {
            // Check if er_wheel already exists in tunnel 1
            const erWheelExistsInTunnel1 = tunnel1.sections.some(
              (s) => s.type === 'er_wheel',
            );
            // If it doesn't exist, add it
            if (!erWheelExistsInTunnel1) {
              const newErWheelSection = createBotSection({ type: 'er_wheel' , name: 'Rotary Heat-Exchanger'});
              applyCommonDefaults(newErWheelSection);
              tunnel1.sections.push(newErWheelSection);
            }
          }
        }
        // When Rotary Htex (er_wheel) is removed from first tunnel,
        // also remove it from the second tunnel
        if (
          oldType === 'er_wheel' &&
          newType !== 'er_wheel' &&
          tunnelIndex === 0 &&
          draft.tunnels.length > 1
        ) {
          const tunnel1 = draft.tunnels[1];
          if (tunnel1) {
            const erWheelIndex = tunnel1.sections.findIndex(
              (s) => s.type === 'er_wheel',
            );
            if (erWheelIndex !== -1) {
              tunnel1.sections.splice(erWheelIndex, 1);
            }
          }
        }
        // Apply fan section rules after type change
        applyOtherSectionRules(draft);
        applyFanSectionRules(draft);
        applyAddReturnAirflowRules(draft);
        applyAddOutsideAirflowRules(draft);
      });
    },
    [setValue, tunnelIndex, sectionIndex],
  );
  /* ---------------------------- FIELD CHANGE HANDLER ---------------------------- */
  const handleSectionFieldChange = useCallback(
    (value: string | number | null, name: string) => {
      setValue((draft: IBotData) => {
        const parsed =
          value === null
            ? undefined
            : typeof value === 'string' && !isNaN(Number(value))
              ? Number(value)
              : value;
        const target = draft.tunnels?.[tunnelIndex]?.sections?.[sectionIndex];
        if (!target) return;

        (target as any)[name] = parsed;
        if (section.type === 'er_wheel') {
          const target = draft.tunnels?.[
            tunnelIndex === 1 ? 0 : 1
          ]?.sections?.find((s) => s.type === 'er_wheel');
          if (!target) return;

          (target as any)[name] = parsed;
        }
      });
    },
    [setValue, tunnelIndex, sectionIndex],
  );
  /* -------------------------------- SELECT SECTION ------------------------------- */
  const handleSelect = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (isSelected) return;
      if (
        e.target instanceof HTMLElement &&
        e.target.closest('.custom-select__control')
      ) {
        return;
      }
      onSelect?.(tunnelIndex, sectionIndex);
    },
    [onSelect, tunnelIndex, sectionIndex, isSelected],
  );
  // Check if this er_wheel section should be protected from removal
  const isProtectedErWheel = useMemo(() => {
    if (section.type !== 'er_wheel') return false;

    // Get rules to check if rotary_htex is enabled
    const airSystemId = botData.air_system_id ?? 1;
    const tunnelConfig = botData.tunnel_configuration ?? 1;
    const saEaConfig = botData.sa_ea_intake_configuration;
    const rules = getAHURules(airSystemId, tunnelConfig, saEaConfig);

    // Protect er_wheel in tunnel 0 if rotary_htex rule is active
    if (tunnelIndex === 0 && rules?.rotary_htex === true) {
      return true;
    }

    // Protect er_wheel in tunnel 1 if tunnel 0 has an er_wheel section
    if (tunnelIndex === 1) {
      const tunnel0 = botData.tunnels?.[0];
      if (!tunnel0) return false;
      return tunnel0.sections.some((s) => s.type === 'er_wheel');
    }

    return false;
  }, [
    section.type,
    tunnelIndex,
    botData.tunnels,
    botData.air_system_id,
    botData.tunnel_configuration,
    botData.sa_ea_intake_configuration,
  ]);

  // Autofocus Select when section is newly added and selected
  useEffect(() => {
    if (isSelected) {
      // Focus the react-select input element after a short delay to ensure DOM is ready
      const timeoutId = setTimeout(() => {
        // Try multiple methods to focus the select
        const selectElement = selectRef.current;
        if (selectElement && typeof selectElement.focus === 'function') {
          // react-select exposes focus() method on the ref
          selectElement.focus();
        } else {
          // Fallback: find the input element in the DOM using the data attribute
          const inputElement = document.querySelector(
            `[data-select-name="section_type_${section.id}"] input`,
          ) as HTMLInputElement;
          if (inputElement) {
            inputElement.focus();
            // Also try to open the menu by clicking the control
            const controlElement = document.querySelector(
              `[data-select-name="section_type_${section.id}"] .custom-select__control`,
            ) as HTMLElement;
            controlElement?.click();
          }
        }
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [isSelected, section.type, section.id]);

  const SectionFields = SectionFieldsMap[section.type];

  /* ---------------------------------- RENDER ---------------------------------- */
  return (
    <>
      <div
        className={`bot-section ${!section.type ? 'empty-section' : ''} ${
          isSelected ? 'selected' : ''
        } `}
        onClick={handleSelect}
      >
        <div className="bot-section__field-group">
          <div className="bot-section__field">
            <Select
              ref={selectRef}
              name={`section_type_${section.id}`}
              value={section.type}
              options={sectionOptions}
              onChange={handleTypeChange}
              className="bot-section__select section-type"
              disabled={
                isProtectedErWheel ||
                isMixedAirPlenum ||
                !canRemoveFanSection(botData, tunnelIndex, sectionIndex)
              }
              border_bottom={false}
            />
            {section.type && <span className="text-span">section</span>}
          </div>
        </div>
        {section.type && (
          <>
            {SectionFields && (
              <SectionFields
                section={section}
                onFieldChange={handleSectionFieldChange}
                tunnel_configuration={botData.tunnel_configuration}
                tunnelIndex={tunnelIndex}
                airflow_direction_id={
                  botData.tunnels[tunnelIndex].airflow_direction_id
                }
                sectionIndex={sectionIndex}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
export default memo(BotAHUSectionComponent);
